import moment from "moment";
import {Button, Placeholder} from "react-bootstrap";
import {useEffect} from "react";
import {Link, useLocation, useParams} from "react-router-dom";

import Util from "@/lib/Util.jsx";
import Alert from "@/components/ui/alert/alert.jsx";
import Title from "@/components/ui/title/title.jsx";
import FeedbackError from "@/components/ui/feedback-error/feedback-error.jsx";
import AnimatedComponents from "@/components/ui/animated-component/animated-components.jsx";
import {ListDepartureTimes} from "@/components/list-departure-times/list-departure-times.jsx";
import bcAll from "@/components/breadcrumb-app/breadcrumb-context.jsx";
import {dateConfigs} from "@/assets/resources.js";
import {useHistoryData} from "@/hooks/use-history-data.jsx";

const useBreadcrumb = bcAll.useBreadcrumb;

moment.locale(dateConfigs.lang);

export default function HistoryDayDepartureTimes() {
  const {id} = useParams();
  const {pathname} = useLocation();
  
  const departureTimeDate = id;
  const departureTimeDateIsValid = departureTimeDate.match(/^(\d+)X(\d+)X(\d+)$/) || false;
  
  const departureTimeDateFormatted = departureTimeDate.split("X").toReversed().join(".");
  const departureTimeMomentInstance = moment(`${departureTimeDate.split("X").join("-")}T00:00:00-03:00`);
  
  const lineId = pathname.split("/")[pathname.split("/").length - 2];
  
  const {setLabel} = useBreadcrumb();
  const {error, loaded, data, lineData} = useHistoryData(Util.checkIsValid(lineId) ? lineId : null, null);
  
  useEffect(() => {
    document.title = "Mobilidade - Histórico de horários";
    
    setLabel("history", "Histórico");
    setLabel("departure-times", "Horários de partida");
    
    if (lineData?.[0]) {
      const lineLabel = (`${lineData[0]["line_number"] || "Linha"} - ` + (lineData[0]["line_name"] ? lineData[0]["line_name"] : ""))?.replaceAll("/", " ⇄ ");
      setLabel(lineId, lineLabel);
    }
    
    if (departureTimeDate && departureTimeDateIsValid) {
      setLabel(id, departureTimeDateFormatted);
    }
  }, [lineData, departureTimeDate, departureTimeDateIsValid, lineId, id, departureTimeDateFormatted]);
  
  if (!lineId || !Util.checkIsValid(lineId)) return <Alert variant={'danger'} margin={"mt-0"}>O id da linha não foi informado.</Alert>
  else if (!Util.checkIsValid(departureTimeDate.replace(/\D/, ""))) return <Alert variant={'danger'} margin={"mt-0"}>A data do histórico não foi informada.</Alert>
  else if (!departureTimeDateIsValid) return <Alert variant={'danger'} margin={"mt-0"}>A data do histórico não é válida.</Alert>
  
  if (loaded) return (
    <AnimatedComponents>
      <Placeholder as="h1" animation="glow"><Placeholder xs={6}/></Placeholder>
      <Placeholder as="h2" animation="glow"><Placeholder xs={4}/></Placeholder>
    </AnimatedComponents>
  )
  else if (error) {
    console.error(error);
    return <FeedbackError code={error.response ? error.response.status || 500 : 500} text={error.message} type={'card'}/>;
  } else if (data.length === 0) return <Alert variant={'danger'} margin={"mt-0"}>Histórico não localizado.</Alert>;
  //
  else if (departureTimeDateIsValid && departureTimeMomentInstance.isValid()) {
    return (
      <AnimatedComponents>
        <h1 className={"m-0 p-0"}><span className={"text-body-secondary"}>Histórico de horários</span></h1>
        <Link to={`/lines/${lineId}`} className={"text-decoration-none"}>
          <Title type={"h2"} classX=" fs-3 d-inline mt-1 p-0 d-block mb-0">
            <span className="d-block text-body" style={{fontSize: "inherit"}}>Linha {(lineData?.[0]?.["line_number"] + " - " + lineData?.[0]?.["departure_location"] + " ⇄ " + lineData?.[0]?.["destination_location"] || "")?.replaceAll("/", " ⇄ ")}</span>
          </Title>
        </Link>
        
        <h1 className={"m-0 p-0"}>
          <span className="d-block fs-6 text-body-secondary mt-2 mb-3" style={{fontSize: "inherit"}}>Snapshot dos horários de partida em {Util.renderText(departureTimeMomentInstance.format("DD"))} de {Util.translateMonth(departureTimeMomentInstance.format("MMMM")?.toLowerCase())} de {departureTimeMomentInstance.format("YYYY")}</span>
        </h1>
        
        <Alert variant={"warning"}>
          <p className={"m-0"}>Você está vendo horários da linha {lineData?.[0]?.["line_number"] || ""} que <b>foram atualizados em {Util.renderText(departureTimeMomentInstance.format("DD"))} de {Util.translateMonth(departureTimeMomentInstance.format("MMMM")?.toLowerCase())} de {departureTimeMomentInstance.format("YYYY")}.</b> <Link to={`/lines/${lineId}`} className={"text-warning-emphasis"}>Clique aqui para visualizar os horários de partidas atuais.</Link></p>
        </Alert>
        
        <section className={"d-flex flex-column gap-4 gap-sm-5 align-items-start mt-5"}>
          {lineData && <ListDepartureTimes line_id={parseInt(lineId)} departure_location={lineData?.[0]?.["departure_location"]} destination_location={lineData?.[0]?.["destination_location"]} variant={{type: "history", departureTimeDate: departureTimeMomentInstance.add(1, "day").format("YYYY-MM-DD")}}/>}
        </section>
        
        <Link to={`/lines/${lineId}`} className={"d-inline-flex mt-3 text-decoration-none"}>
          <Button variant={"primary"}>Veja todas as informações da linha {lineData?.[0]?.["line_number"] ?? ""}</Button>
        </Link>
      </AnimatedComponents>
    );
  }
}
